import { DmnModel, Rule, ColumnDefinition } from '../types';

export const evaluateDmn = (model: DmnModel, inputs: Record<string, any>) => {
  const inputCols = model.columns.filter(c => c.type === 'input');
  const outputCols = model.columns.filter(c => c.type === 'output');
  
  const matches: Rule[] = [];

  for (const rule of model.rules) {
    let match = true;
    for (const col of inputCols) {
      const cellValue = rule.cells[col.id];
      const inputValue = inputs[col.variableName || col.label];
      
      if (!checkMatch(cellValue, inputValue, col.dataType)) {
        match = false;
        break;
      }
    }
    if (match) matches.push(rule);
  }

  if (matches.length === 0) return [];

  // Hit Policy Implementation (Simplified)
  // UNIQUE, FIRST, PRIORITY: Return first match (PRIORITY usually requires output order definition, using FIRST as fallback)
  if (['UNIQUE', 'FIRST', 'PRIORITY'].includes(model.hitPolicy)) {
      return [extractOutputs(matches[0], outputCols)];
  }
  
  // COLLECT, RULE ORDER, ANY, OUTPUT ORDER: Return all matches (Order logic simplified to rule order)
  return matches.map(r => extractOutputs(r, outputCols));
};

const extractOutputs = (rule: Rule, outputCols: ColumnDefinition[]) => {
    const out: Record<string, any> = {};
    outputCols.forEach(c => {
        out[c.variableName || c.label] = rule.cells[c.id];
    });
    return out;
};

const checkMatch = (cell: string, input: any, type: string): boolean => {
    // Empty cell or dash means "match anything"
    if (!cell || cell === '-' || cell.trim() === '') return true;
    const cleanCell = cell.trim();
    
    // If input is missing/null but rule specifies a constraint, it doesn't match
    if (input === undefined || input === null || input === '') return false;

    if (type === 'string') {
        // Strip quotes: "Contractor" -> Contractor
        const target = cleanCell.replace(/^"(.*)"$/, '$1');
        return target === String(input);
    }
    
    if (type === 'boolean') {
        const cellBool = cleanCell.toLowerCase() === 'true';
        const inputBool = String(input).toLowerCase() === 'true';
        return cellBool === inputBool;
    }
    
    if (type === 'number') {
        const numInput = Number(input);
        if (isNaN(numInput)) return false;

        // Simple FEEL parsing
        if (cleanCell.startsWith('>=')) return numInput >= Number(cleanCell.substring(2));
        if (cleanCell.startsWith('<=')) return numInput <= Number(cleanCell.substring(2));
        if (cleanCell.startsWith('>')) return numInput > Number(cleanCell.substring(1));
        if (cleanCell.startsWith('<')) return numInput < Number(cleanCell.substring(1));
        
        // Range [min..max]
        const rangeMatch = cleanCell.match(/^\[(\d+(?:\.\d+)?)\.\.(\d+(?:\.\d+)?)\]$/);
        if (rangeMatch) {
            return numInput >= Number(rangeMatch[1]) && numInput <= Number(rangeMatch[2]);
        }

        return numInput === Number(cleanCell);
    }

    // Default string equality
    return cleanCell == input;
};